import React, { useMemo } from 'react';
import ReactSelect from 'react-select';
import { Input } from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

const selectStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: '44px',
        borderRadius: '2px',
        borderColor: state.isFocused ? '#FF5733' : '#e5e7eb',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(255,87,51,0.1)' : 'none',
        '&:hover': { borderColor: state.isDisabled ? '#e5e7eb' : '#ff6f45' }
    }),
    singleValue: (provided) => ({ ...provided, color: '#2a2e30', fontSize: '0.9rem' }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? '#fff7ed' : state.isSelected ? '#ffedd5' : '#ffffff',
        color: state.isSelected ? '#7c2d12' : '#111827'
    }),
    dropdownIndicator: (provided, state) => ({ ...provided, color: state.isDisabled ? '#9ca3af' : '#FF5733' }),
    indicatorSeparator: () => ({ display: 'none' }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 })
};

const ExpenseFilters = ({
    filters,
    setFilters,
    categories,
    subCategories,
    cars,
    handleCategoryChange,
    clearFilters,
    showApprovalStatus = true
}) => {
    const typeOptions = [
        { value: '', label: 'All Types' },
        { value: 'VEHICLE', label: 'Vehicle' },
        { value: 'OFFICE', label: 'Office' }
    ];

    const categoryOptions = useMemo(() => [
        { value: '', label: 'All Categories' },
        ...(categories || []).map(cat => ({ value: cat.category_id?.toString() ?? '', label: cat.category_name }))
    ], [categories]);

    const subCategoryOptions = useMemo(() => [
        { value: '', label: 'All Sub-Categories' },
        ...(subCategories || []).map(sub => ({ value: sub.sub_category_id?.toString() ?? '', label: sub.sub_category_name }))
    ], [subCategories]);

    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: 'PENDING', label: 'Pending' },
        { value: 'APPROVED', label: 'Approved' },
        { value: 'REJECTED', label: 'Rejected' }
    ];

    const vehicleOptions = useMemo(() => [
        { value: '', label: 'All Vehicles' },
        ...(cars || []).map(car => ({ value: car.plate_no, label: car.plate_no }))
    ], [cars]);

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-200">
            <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Expense Type</label>
                <ReactSelect
                    options={typeOptions}
                    value={typeOptions.find(o => o.value === filters.expense_type) || typeOptions[0]}
                    onChange={(opt) => setFilters({ ...filters, expense_type: opt?.value ?? '' })}
                    styles={selectStyles}
                    isSearchable={false}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                />
            </div>

            <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Category</label>
                <ReactSelect
                    options={categoryOptions}
                    value={categoryOptions.find(o => o.value === filters.category) || categoryOptions[0]}
                    onChange={(opt) => handleCategoryChange(opt?.value ?? '')}
                    styles={selectStyles}
                    isSearchable={true}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                />
            </div>

            <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Sub-Category</label>
                <ReactSelect
                    options={subCategoryOptions}
                    value={subCategoryOptions.find(o => o.value === filters.sub_category) || subCategoryOptions[0]}
                    onChange={(opt) => setFilters({ ...filters, sub_category: opt?.value ?? '' })}
                    styles={selectStyles}
                    isSearchable={true}
                    isDisabled={!filters.category}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                />
            </div>

            {showApprovalStatus && (
                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">Approval Status</label>
                    <ReactSelect
                        options={statusOptions}
                        value={statusOptions.find(o => o.value === filters.approval_status) || statusOptions[0]}
                        onChange={(opt) => setFilters({ ...filters, approval_status: opt?.value ?? '' })}
                        styles={selectStyles}
                        isSearchable={false}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        menuPosition="fixed"
                    />
                </div>
            )}

            <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Vehicle</label>
                <ReactSelect
                    options={vehicleOptions}
                    value={vehicleOptions.find(o => o.value === filters.plate_no) || vehicleOptions[0]}
                    onChange={(opt) => setFilters({ ...filters, plate_no: opt?.value ?? '' })}
                    styles={selectStyles}
                    isSearchable={true}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                />
            </div>

            <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Min Amount</label>
                <Input
                    type="number"
                    value={filters.min_amount}
                    onChange={(e) => setFilters({ ...filters, min_amount: e.target.value })}
                    className="text-right"
                    placeholder="0"
                />
            </div>

            <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Max Amount</label>
                <Input
                    type="number"
                    value={filters.max_amount}
                    onChange={(e) => setFilters({ ...filters, max_amount: e.target.value })}
                    className="text-right"
                    placeholder="0"
                />
            </div>

            <div className="flex flex-col justify-end">
                <button
                    type="button"
                    onClick={clearFilters}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-600 hover:border-[#FF5733] hover:text-[#FF5733] text-sm font-semibold rounded-sm transition-colors"
                >
                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                    Clear Filters
                </button>
            </div>
        </div>
    );
};

export default ExpenseFilters;
